/**
 * Social-account LINKING intent — persisted across the OAuth redirect.
 *
 * Sibling of `oauthIntent.ts` (sign-in/sign-up intent) and uses the exact same
 * dual-store strategy: the payload is written to BOTH sessionStorage (primary)
 * and localStorage (fallback) because on native the app WebView can be reclaimed
 * while it is backgrounded during the Chrome Custom Tab OAuth round-trip — and
 * some browsers (Safari iOS, certain in-app webviews, Apple redirects) drop
 * sessionStorage across the provider round-trip. A raw sessionStorage read would
 * then miss a real in-flight linking session and the return would be mishandled
 * as a normal login (the "404 / mishandled linking return" symptom on native).
 *
 * A 10-minute TTL is enforced so a stale linking session from a previous,
 * abandoned attempt can never hijack a later sign-in.
 *
 * Presence of this key tells:
 *   - useAuthState.onAuthStateChange (SIGNED_IN) to run completeSocialLink()
 *     instead of the normal profile-load / social-swap path, and
 *   - App.tsx's native appUrlOpen listener to route the deep-link return through
 *     the linking-completion path (→ /dashboard/settings) instead of the normal
 *     login pipeline (→ /auth/callback).
 */

const KEY = 'behtari_linking_intent';
const TTL_MS = 10 * 60 * 1000;

/**
 * In-flight freshness window for the reentrancy-sensitive completion path
 * (useAuthState's onAuthStateChange SIGNED_IN branch). `ts` is stamped by
 * setLinkingIntent() at the moment initiateSocialLink() redirects to the
 * provider. A genuine OAuth round-trip completes in seconds; if the stored
 * intent is older than this, it is a STALE flag left by an interrupted attempt
 * and must NOT drive the completion branch — otherwise a plain SIGNED_IN /
 * session restore on an ordinary page load would walk into the signOut/
 * refreshSession reentrancy deadlock. Deliberately much tighter than TTL_MS.
 */
export const LINKING_INFLIGHT_MS = 2 * 60 * 1000;

export type LinkingIntent = {
  provider: 'google' | 'apple';
  /** The original CNIC user id to restore the session back to after OAuth. */
  userId: string;
  /** The original CNIC session refresh token, used to restore it. */
  refreshToken: string;
  ts?: number;
};

export function setLinkingIntent(intent: LinkingIntent): void {
  const payload = JSON.stringify({ ...intent, ts: Date.now() });
  try { sessionStorage.setItem(KEY, payload); } catch { /* no-op */ }
  try { localStorage.setItem(KEY, payload); } catch { /* no-op */ }
}

export function getLinkingIntent(): LinkingIntent | null {
  const read = (store: Storage | undefined): LinkingIntent | null => {
    try {
      const raw = store?.getItem(KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LinkingIntent;
      if (parsed.ts && Date.now() - parsed.ts > TTL_MS) return null;
      if (!parsed.provider || !parsed.userId || !parsed.refreshToken) return null;
      return parsed;
    } catch {
      return null;
    }
  };
  try {
    return read(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined)
      ?? read(typeof localStorage !== 'undefined' ? localStorage : undefined);
  } catch {
    return null;
  }
}

export function clearLinkingIntent(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* no-op */ }
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}

export function isLinkingInProgress(): boolean {
  return getLinkingIntent() !== null;
}

/**
 * Diagnostic-only snapshot of both storage layers — never mutates anything.
 * `getLinkingIntent()`/`isLinkingInProgress()` collapse "never written",
 * "expired", and "malformed" into the same `false`/`null`, which makes it
 * impossible to tell from a failed link attempt whether the flag was ever
 * set at all. This exists purely so call sites can log which of those cases
 * actually occurred on a real device, instead of guessing.
 */
export function debugLinkingIntentSnapshot(): {
  sessionStorageHasFlag: boolean;
  localStorageHasFlag: boolean;
  ageMs: number | null;
  isValid: boolean;
} {
  const inspect = (store: Storage | undefined): { present: boolean; ageMs: number | null } => {
    try {
      const raw = store?.getItem(KEY);
      if (!raw) return { present: false, ageMs: null };
      const parsed = JSON.parse(raw) as LinkingIntent;
      return { present: true, ageMs: parsed.ts ? Date.now() - parsed.ts : null };
    } catch {
      return { present: true, ageMs: null };
    }
  };
  const s = inspect(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
  const l = inspect(typeof localStorage !== 'undefined' ? localStorage : undefined);
  return {
    sessionStorageHasFlag: s.present,
    localStorageHasFlag: l.present,
    ageMs: s.ageMs ?? l.ageMs,
    isValid: isLinkingInProgress(),
  };
}
